import {
  ErrorMessage,
  SelectButton,
  SelectButtonProps,
  ZipCodeInput,
} from '@hc-frontend/core-ui';
import { FormEvent, useState } from 'react';
import { z } from 'zod';

import {
  PLAN_YEAR,
  PLAN_YEAR_SCHEMA,
  ZIP_CODE,
  ZIP_CODE_SCHEMA,
} from './constants';
import { FormButtons } from './form-buttons';
import { useSubsidyCalculator } from './provider';

const FormSchema = z.object({
  zipCode: ZIP_CODE_SCHEMA,
  planYear: PLAN_YEAR_SCHEMA,
});

export function LocationStep() {
  const { setStep } = useSubsidyCalculator();
  const { answers, setAnswer } = useSubsidyCalculator();
  const [zipCode, setZipCode] = useState(answers[ZIP_CODE] ?? '');
  const [planYear, setPlanYear] = useState(answers[PLAN_YEAR] ?? '');

  const [errorPlanYear, setErrorPlanYear] = useState<string | null>(null);

  const [errorZipCode, setErrorZipCode] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorPlanYear(null);
    setErrorZipCode(null);
    const result = FormSchema.safeParse({ zipCode, planYear });
    if (result.success) {
      setAnswer(ZIP_CODE, result.data.zipCode);
      setAnswer(PLAN_YEAR, result.data.planYear);
      setStep((v) => v + 1);
    } else {
      const fields = result.error.format();

      if (fields.zipCode) {
        setErrorZipCode('Please enter a valid zip code.');
      }
      if (fields.planYear) {
        setErrorPlanYear('Please select a valid plan year.');
      }
    }
  };

  const today = new Date(Date.now());
  const currentYear = today.getFullYear();
  const nextYear = currentYear + 1;
  // Only display the next year as an option if the current month is October, November, or December.
  const showNextYear = today.getMonth() > 8;

  const yearOptions: SelectButtonProps['options'] = showNextYear
    ? [
        {
          id: currentYear.toString(),
          label: currentYear.toString(),
          value: currentYear.toString(),
          checked: currentYear.toString() === planYear,
        },
        {
          id: nextYear.toString(),
          label: nextYear.toString(),
          value: nextYear.toString(),
          checked: nextYear.toString() === planYear,
        },
      ]
    : [
        {
          id: currentYear.toString(),
          label: currentYear.toString(),
          value: currentYear.toString(),
          checked: currentYear.toString() === planYear,
        },
      ];

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="grid grid-cols-1 gap-16-16">
        <SelectButton
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both"
          name="year"
          options={yearOptions}
          label="Plan Year"
          onChange={(e) => setPlanYear(e.target.value)}
          errorText={
            errorPlanYear ? (
              <ErrorMessage>{errorPlanYear}</ErrorMessage>
            ) : undefined
          }
        />

        <ZipCodeInput
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-150 fill-mode-both"
          label="Zip Code"
          placeholder="e.g. 33133"
          helpText="Enter your 5-digit ZIP code"
          withButton={false}
          name={ZIP_CODE}
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          errorText={
            errorZipCode ? (
              <ErrorMessage>{errorZipCode}</ErrorMessage>
            ) : undefined
          }
        />
      </div>

      <FormButtons className="animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-300 fill-mode-both" />
    </form>
  );
}
